import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Insights as VisualisationIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import Visualiser from '../pages/Visualiser';
// LazyPointsMap defers maplibre-gl to an async chunk. This registry is in the eager/initial
// bundle, so a static PointsMap import here would keep maplibre-gl in the main chunk.
import PointsMap from '../components/gis/LazyPointsMap';
import { CHART_UNAVAILABLE_MESSAGE } from './chartRendering';
import {
  AddonExtension,
  AddonHostExtensionPoint,
  DashboardWidgetSourceOption,
} from '../types/addons';
import {
  DashboardQuerySourceType,
  DashboardWidgetDto,
  DashboardWidgetRunResult,
  DashboardWidgetType,
} from '../types/dashboards';

export interface AddonWorkspaceHomeCardProps {
  extension: AddonExtension;
}

export interface AddonDataObjectActionProps {
  extension: AddonExtension;
  dataObject?: unknown;
}

export interface AddonAdminSettingsPanelProps {
  extension: AddonExtension;
}

export interface AddonAuditEventViewerProps {
  extension: AddonExtension;
  auditEvent?: unknown;
}

export interface AddonDashboardWidgetProps {
  extension: AddonExtension;
  widget?: DashboardWidgetDto;
  result?: DashboardWidgetRunResult;
  enablePrintCapture?: boolean;
}

export type AddonHostComponentPropsByPoint = {
  'workspace.home.card': AddonWorkspaceHomeCardProps;
  'dataObject.readAction': AddonDataObjectActionProps;
  'admin.settings.panel': AddonAdminSettingsPanelProps;
  'audit.event.viewer': AddonAuditEventViewerProps;
  'dashboard.widget': AddonDashboardWidgetProps;
};

export type AddonHostComponent<P extends AddonHostExtensionPoint> =
  React.ComponentType<AddonHostComponentPropsByPoint[P]>;

export type AddonHostComponentRegistry = {
  [P in AddonHostExtensionPoint]: Record<string, AddonHostComponent<P>>;
};

const VisualisationWorkspaceHomeCard: AddonHostComponent<'workspace.home.card'> = ({
  extension,
}) => {
  const navigate = useNavigate();
  const label =
    typeof extension.manifest.label === 'string' && extension.manifest.label.trim()
      ? extension.manifest.label
      : 'Visualisation Studio';

  return (
    <Card
      variant="outlined"
      data-testid="addon-workspace-card-visualisation"
      sx={{ height: '100%' }}
    >
      <CardActionArea
        onClick={() => navigate('/visualisations')}
        sx={{ height: '100%', alignItems: 'stretch' }}
      >
        <CardHeader
          avatar={<VisualisationIcon color="primary" />}
          title={label}
          subheader="Visualisation Studio"
          titleTypographyProps={{ variant: 'subtitle1' }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack direction="row" spacing={1}>
            <Chip label="Charts" size="small" />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
const HiddenDataObjectAction: AddonHostComponent<'dataObject.readAction'> = () => null;
const HiddenAdminSettingsPanel: AddonHostComponent<'admin.settings.panel'> = () => null;
const HiddenAuditEventViewer: AddonHostComponent<'audit.event.viewer'> = () => null;

// Chart artifacts are not drawn yet (see CHART_RENDERING_AVAILABLE); a saved chart widget keeps
// its settings and says so instead of implying a finished chart.
const VisualisationDashboardChartWidget: AddonHostComponent<'dashboard.widget'> = ({ widget }) => {
  const chartType = String(widget?.widgetType?.toLowerCase() || 'chart');
  const artifactName = String(widget?.title || 'Visualisation chart');

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="visualisation-dashboard-chart-widget">
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label="Visualisation" size="small" color="primary" variant="outlined" />
          <Chip label={chartType.toUpperCase()} size="small" />
        </Stack>
        <Typography variant="subtitle1">{artifactName}</Typography>
        <Alert severity="info">{CHART_UNAVAILABLE_MESSAGE}</Alert>
      </Stack>
    </Paper>
  );
};

const GisDashboardMapLayerWidget: AddonHostComponent<'dashboard.widget'> = ({
  widget,
  result,
  enablePrintCapture = false,
}) => {
  const resultPayload = asRecord(result?.result);
  const artifactName = String(widget?.title || 'GIS map layer');
  const rows = resultPayload && Array.isArray(resultPayload.rows)
    ? resultPayload.rows.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object' && !Array.isArray(row))
    : [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="gis-dashboard-map-layer-widget">
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label="GIS" size="small" color="primary" variant="outlined" />
          <Chip label="Map layer" size="small" />
        </Stack>
        <Box>
          <Typography variant="subtitle1">{artifactName}</Typography>
        </Box>
        <PointsMap
          rows={rows}
          totalRowCount={rows.length}
          height={260}
          testId="gis-widget-map"
          capturable={enablePrintCapture}
        />
      </Stack>
    </Paper>
  );
};

export const ROUTE_COMPONENTS: Record<string, React.ReactElement> = {
  'visualisation.studio.route': <Visualiser />,
};

export const NAV_ICONS: Record<string, React.ReactElement> = {
  Insights: <VisualisationIcon />,
};

export const HOST_COMPONENTS: AddonHostComponentRegistry = {
  'workspace.home.card': {
    'visualisation.workspace.recentCharts': VisualisationWorkspaceHomeCard,
  },
  'dataObject.readAction': {
    'visualisation.dataObject.inspectAction': HiddenDataObjectAction,
    'gis.dataObject.viewOnMapAction': HiddenDataObjectAction,
  },
  'admin.settings.panel': {
    'visualisation.admin.settingsPanel': HiddenAdminSettingsPanel,
  },
  'audit.event.viewer': {
    'visualisation.audit.eventViewer': HiddenAuditEventViewer,
  },
  'dashboard.widget': {
    'visualisation.dashboard.chartWidget': VisualisationDashboardChartWidget,
    'gis.dashboard.mapLayerWidget': GisDashboardMapLayerWidget,
  },
};

const getOrder = (extension: AddonExtension): number =>
  typeof extension.manifest.order === 'number' ? extension.manifest.order : 500;

const orderExtensions = (extensions: AddonExtension[]): AddonExtension[] =>
  [...extensions].sort((left, right) => {
    const orderDelta = getOrder(left) - getOrder(right);
    if (orderDelta !== 0) {
      return orderDelta;
    }
    return `${left.addonId}:${left.extensionKey}`.localeCompare(`${right.addonId}:${right.extensionKey}`);
  });

const dedupeExtensions = (
  extensions: AddonExtension[],
  keyForExtension: (extension: AddonExtension) => string | null
): AddonExtension[] => {
  const seen = new Set<string>();
  return extensions.filter(extension => {
    const key = keyForExtension(extension);
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const getAddonRoutePath = (extension: AddonExtension): string | null => {
  const path = extension.manifest.path || extension.extensionKey;
  return typeof path === 'string' && path.startsWith('/') ? path : null;
};

export const getAddonRouteComponentKey = (extension: AddonExtension): string | null => {
  const componentKey = extension.manifest.componentKey || extension.manifest.routeComponentKey;
  return typeof componentKey === 'string' && componentKey.length > 0 ? componentKey : null;
};

export const renderAddonRoute = (extension: AddonExtension): React.ReactElement | null => {
  const componentKey = getAddonRouteComponentKey(extension);
  if (!componentKey || !ROUTE_COMPONENTS[componentKey]) {
    return null;
  }
  return ROUTE_COMPONENTS[componentKey];
};

export const hasCompiledRouteForExtension = (extension: AddonExtension): boolean =>
  Boolean(getAddonRoutePath(extension) && renderAddonRoute(extension));

export const getAddonNavIcon = (icon?: string): React.ReactElement | null =>
  icon && NAV_ICONS[icon] ? NAV_ICONS[icon] : null;

export const hasCompiledMainNavForExtension = (extension: AddonExtension): boolean =>
  Boolean(
    extension.extensionPoint === 'main.nav'
    && getAddonRoutePath(extension)
    && renderAddonRoute(extension)
    && getAddonNavIcon(extension.manifest.icon)
  );

export const getCompiledAddonRouteExtensions = (
  extensions: AddonExtension[]
): AddonExtension[] =>
  dedupeExtensions(
    orderExtensions(
      extensions.filter(
        extension => extension.extensionPoint === 'addon.route' && hasCompiledRouteForExtension(extension)
      )
    ),
    getAddonRoutePath
  );

export const getCompiledMainNavExtensions = (extensions: AddonExtension[]): AddonExtension[] =>
  dedupeExtensions(
    orderExtensions(extensions.filter(hasCompiledMainNavForExtension)),
    extension => extension.extensionKey
  );

export const getCompiledAddonHostComponent = <P extends AddonHostExtensionPoint>(
  extensionPoint: P,
  extension: AddonExtension
): AddonHostComponent<P> | null => {
  const componentKey = extension.manifest.componentKey;
  if (
    extension.extensionPoint !== extensionPoint
    || typeof componentKey !== 'string'
    || !HOST_COMPONENTS[extensionPoint][componentKey]
  ) {
    return null;
  }
  return HOST_COMPONENTS[extensionPoint][componentKey];
};

export const getCompiledHostSlotExtensions = <P extends AddonHostExtensionPoint>(
  extensions: AddonExtension[],
  extensionPoint: P
): AddonExtension[] =>
  dedupeExtensions(
    orderExtensions(
      extensions.filter(extension => Boolean(getCompiledAddonHostComponent(extensionPoint, extension)))
    ),
    extension => extension.extensionKey
  );

export const getCompiledDashboardWidgetSourceOptions = (
  extensions: AddonExtension[]
): DashboardWidgetSourceOption[] =>
  dedupeExtensions(
    orderExtensions(
      extensions.filter(extension =>
        Boolean(getCompiledAddonHostComponent('dashboard.widget', extension))
      )
    ),
    extension => dashboardWidgetSourceTypeForComponent(extension.manifest.componentKey || null)
  ).flatMap(extension => {
    const componentKey = extension.manifest.componentKey;
    const sourceType = dashboardWidgetSourceTypeForComponent(componentKey || null);
    if (!componentKey || !sourceType) {
      return [];
    }
    return [
      {
        extension,
        componentKey,
        label: stringValue(extension.manifest.label) || sourceType,
        sourceType,
        defaultWidgetType: defaultWidgetTypeForSourceType(sourceType),
        artifactTypes: arrayOfStrings(extension.manifest.artifactTypes),
        capabilities: arrayOfStrings(extension.manifest.capabilities),
      },
    ];
  });

function dashboardWidgetSourceTypeForComponent(
  componentKey: string | null
): DashboardQuerySourceType | null {
  switch (componentKey) {
    case 'visualisation.dashboard.chartWidget':
      return 'VISUALISATION_ARTIFACT';
    case 'gis.dashboard.mapLayerWidget':
      return 'GIS_ARTIFACT';
    default:
      return null;
  }
}

function defaultWidgetTypeForSourceType(
  sourceType: DashboardQuerySourceType
): DashboardWidgetType {
  switch (sourceType) {
    case 'VISUALISATION_ARTIFACT':
      return 'BAR';
    case 'GIS_ARTIFACT':
      return 'MAP';
    default:
      return 'TABLE';
  }
}

function arrayOfStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}
